import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { type PropsWithChildren, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Tournament } from '@/api/cms/tournaments';
import { Countdown } from '@/components/Countdown';
import { GamesGrid } from '@/components/GamesGrid';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { VerticalAlign } from '@/components/Modal/styled';
import { SectionTitle } from '@/components/SectionTitle';
import { StaticModal } from '@/components/StaticModal';
import { ScoreTable } from '@/components/tournaments';
import { ThemeMode } from '@/helpers/enums';
import { getTimeLeft } from '@/helpers/timer';
import { useGetCommonStore } from '@/store';
import { ScTermsAndConditions, ScTournamentDetailsModal } from './styled';

const getGameGridParams = (isMobileDevice: boolean) => ({
  perChunkObj: {
    desktopL: 8,
    desktop: 8,
    tablet: 8,
    mobile: 8,
    mobileS: 8,
  },
  gridOptions: {
    columns: 4,
    columnsTablet: 4,
    columnsMobile: 4,
    columnsMobileS: 4,
    columnGap: isMobileDevice ? 8 : 12,
    rowGap: isMobileDevice ? 8 : 12,
  },
  plugGray: isMobileDevice,
});

interface TournamentModalProps extends Tournament {
  onClose: () => void;
}

export const TournamentModal = ({
  name,
  banner: { minBet, prizePool, durationTo },
  backendTournamentId,
  collectionName,
  text,
  icon,
  onClose,
}: PropsWithChildren<TournamentModalProps>) => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const termsRef = useRef<HTMLDivElement | null>(null);

  const [isTermsAndConditionsOpen, setIsTermsAndConditionsOpen] =
    useState<boolean>(false);
  const [isTournamentGamesTitle, setIsTournamentGamesTitle] = useState(false);

  const timeLeft = useMemo(() => getTimeLeft(durationTo), [durationTo]);

  const tournamentIcon = useMemo(
    () => (
      <ImageComponent
        alt="tournament"
        className="tournament-icon"
        height={76}
        src={icon?.url || '/cup.webp'}
        width={76}
      />
    ),
    [icon]
  );

  return (
    <StaticModal
      icon={<IconComponent name="cup" size="40px" />}
      isPortal
      maxWidth={640}
      onClose={onClose}
      title={t('tournaments.modalTitle')}
      verticalAlign={VerticalAlign.TOP}
    >
      <ScTournamentDetailsModal>
        <header>
          {isMobileDevice && <h2>{name}</h2>}
          <div>
            {!isMobileDevice && <h2>{name}</h2>}
            <div className="details">
              <div>
                <div className="icon">
                  <IconComponent name="clockIcon" size="18px" />
                </div>

                <Countdown dual timeLeft={timeLeft}>
                  {({ days, hours, minutes, seconds }) => (
                    <span>
                      <span attr-label="dd">
                        {days}
                        {t('timer.dd')[0]}
                      </span>
                      :
                      <span attr-label="hh">
                        {hours}
                        {t('timer.hh')[0]}
                      </span>
                      :
                      <span attr-label="mm">
                        {minutes}
                        {t('timer.mm')[0]}
                      </span>
                      :
                      <span attr-label="ss">
                        {seconds}
                        {t('timer.ss')[0]}
                      </span>
                    </span>
                  )}
                </Countdown>
              </div>
              {prizePool && (
                <div>
                  <div className="icon">
                    <IconComponent name="maxBonus" size="20px" />
                  </div>
                  <span>
                    {prizePool} {t('tournaments.prizePool')}
                  </span>
                </div>
              )}
              <div>
                <div className="icon">
                  <IconComponent name="betIcon" size="20px" />
                </div>
                <span>
                  {minBet} {t('tournaments.minBet')}
                </span>
              </div>
            </div>
            {isMobileDevice && tournamentIcon}
          </div>
          {!isMobileDevice && tournamentIcon}
        </header>

        {backendTournamentId && (
          <div className="modal-section">
            <ScoreTable
              backendTournamentId={backendTournamentId}
              mode={isMobileDevice ? ThemeMode.Dark : ThemeMode.Light}
            />
          </div>
        )}

        <div className="modal-section">
          {isTournamentGamesTitle && (
            <SectionTitle>{t('qualifyingGames')}</SectionTitle>
          )}

          <GamesGrid
            category={collectionName}
            isSmallCard={isMobileDevice}
            onGamesLoad={() => setIsTournamentGamesTitle(true)}
            {...getGameGridParams(isMobileDevice)}
          />
        </div>

        {text && (
          <ScTermsAndConditions
            isOpen={isTermsAndConditionsOpen}
            ref={termsRef}
          >
            <div
              className="title"
              onClick={() => setIsTermsAndConditionsOpen((s) => !s)}
            >
              {t('common:button.termsAndConditions')}
              <div className="arrow">
                <IconComponent name="whiteArrowRight" size="16px" />
              </div>
            </div>
            <AnimatePresence>
              {isTermsAndConditionsOpen && (
                <motion.div
                  animate={{ opacity: 1, rotateX: -15 }}
                  exit={{
                    opacity: 0,
                    rotateX: 0,
                  }}
                  initial={{
                    opacity: 0,
                    rotateX: 0,
                  }}
                  transition={{ duration: 0.25 }}
                >
                  <div dangerouslySetInnerHTML={{ __html: text }} />
                </motion.div>
              )}
            </AnimatePresence>
          </ScTermsAndConditions>
        )}
      </ScTournamentDetailsModal>
    </StaticModal>
  );
};
